import type { CompanyPatchPayload } from "@repo/app-types/entities/company";
import { TimezoneSelect } from "@repo/ui/Select/timezoneSelect";
import { type FC, useEffect, useState } from "react";

export interface CompanyTimezoneEditorProps {
  /** Null means "no override", i.e. the Eastern default. */
  timezone: string | null;
  isDisabled?: boolean;
  onSave: (updates: CompanyPatchPayload) => Promise<boolean>;
}

/**
 * The organization's timezone. Saves on change like the other single-control
 * organization settings, keeping the selection optimistic and rolling it back
 * if the save fails.
 */
export const CompanyTimezoneEditor: FC<CompanyTimezoneEditorProps> = (
  props,
) => {
  const { timezone, isDisabled, onSave } = props;

  const [selectedTimezone, setSelectedTimezone] = useState(timezone);
  useEffect(() => setSelectedTimezone(timezone), [timezone]);

  const handleChange = async (nextTimezone: string | null) => {
    const previousTimezone = selectedTimezone;
    setSelectedTimezone(nextTimezone);

    const didSave = await onSave({ settings: { timezone: nextTimezone } });
    if (!didSave) {
      setSelectedTimezone(previousTimezone);
    }
  };

  return (
    <div className="space-y-2">
      <TimezoneSelect
        label="Time zone"
        value={selectedTimezone}
        onChange={(nextTimezone) => void handleChange(nextTimezone)}
        defaultOptionLabel="Eastern time (default)"
        isDisabled={isDisabled}
      />
      <p className="text-sm text-muted-foreground">
        Used for reporting periods, coaching, AI-generated content, and outbound
        calling hours.
      </p>
    </div>
  );
};
